import React, { useEffect, useState } from 'react';
import { getTransactions, getForecast } from '../services/api';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#f43f5e', '#6366f1'];

function StatCard({ title, value, subtitle, icon, color }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 card-hover animate-fade-in-up">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className={`text-2xl font-bold mt-1 ${color || 'text-slate-800'}`}>{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className="w-11 h-11 rounded-xl bg-slate-50 flex items-center justify-center text-xl">
          {icon}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ refreshKey }) {
  const [transactions, setTransactions] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getTransactions(500).catch(() => ({ data: { transactions: [], total: 0 } })),
      getForecast().catch(() => ({ data: null })),
    ]).then(([txRes, fcRes]) => {
      setTransactions(txRes.data.transactions || []);
      setForecast(fcRes.data);
      setLoading(false);
    });
  }, [refreshKey]);

  // Computations
  const totalIncome = transactions.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const totalExpenses = transactions.filter((t) => t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);
  const netCashFlow = totalIncome - totalExpenses;

  // Category breakdown
  const catMap = {};
  transactions.forEach((t) => {
    if (t.amount < 0) {
      const cat = t.category || 'Uncategorized';
      catMap[cat] = (catMap[cat] || 0) + Math.abs(t.amount);
    }
  });
  const sortedCats = Object.entries(catMap).sort((a, b) => b[1] - a[1]);

  const doughnutData = {
    labels: sortedCats.map(([c]) => c),
    datasets: [
      {
        data: sortedCats.map(([, v]) => v),
        backgroundColor: COLORS.slice(0, sortedCats.length),
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };

  // Monthly spending bar chart
  const monthMap = {};
  transactions.forEach((t) => {
    if (t.amount < 0 && t.date) {
      const m = t.date.slice(0, 7);
      monthMap[m] = (monthMap[m] || 0) + Math.abs(t.amount);
    }
  });
  const months = Object.keys(monthMap).sort();
  const barData = {
    labels: months.map((m) => {
      const [y, mo] = m.split('-');
      return new Date(y, mo - 1).toLocaleString('default', { month: 'short', year: '2-digit' });
    }),
    datasets: [
      {
        label: 'Monthly Spending',
        data: months.map((m) => monthMap[m]),
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
        borderRadius: 8,
        barPercentage: 0.6,
      },
    ],
  };

  const fmt = (n) =>
    '$' +
    Math.abs(n)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-28 rounded-2xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="skeleton h-72 rounded-2xl" />
          <div className="skeleton h-72 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Transactions"
          value={transactions.length}
          subtitle="All time"
          icon="💳"
        />
        <StatCard
          title="Total Income"
          value={fmt(totalIncome)}
          subtitle="Inflows"
          icon="📥"
          color="text-emerald-600"
        />
        <StatCard
          title="Total Expenses"
          value={fmt(totalExpenses)}
          subtitle="Outflows"
          icon="📤"
          color="text-red-500"
        />
        <StatCard
          title="Net Cash Flow"
          value={`${netCashFlow >= 0 ? '+' : '-'}${fmt(netCashFlow)}`}
          subtitle={netCashFlow >= 0 ? 'Positive' : 'Negative'}
          icon={netCashFlow >= 0 ? '📈' : '📉'}
          color={netCashFlow >= 0 ? 'text-emerald-600' : 'text-red-500'}
        />
      </div>

      {transactions.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-slate-100 text-center">
          <div className="text-5xl mb-4">📂</div>
          <h3 className="text-lg font-semibold text-slate-700">No Data Yet</h3>
          <p className="text-slate-400 mt-2">
            Upload a CSV file in the <strong>Upload</strong> tab to get started.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Doughnut */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-fade-in-up">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Spending by Category</h3>
            <div className="max-w-xs mx-auto">
              <Doughnut
                data={doughnutData}
                options={{
                  cutout: '65%',
                  plugins: {
                    legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, pointStyleWidth: 10, font: { size: 12 } } },
                  },
                }}
              />
            </div>
          </div>

          {/* Bar */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-fade-in-up">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Monthly Spending</h3>
            {months.length > 0 ? (
              <Bar
                data={barData}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: {
                    y: { beginAtZero: true, ticks: { callback: (v) => `$${v}` }, grid: { color: '#f1f5f9' } },
                    x: { grid: { display: false } },
                  },
                }}
              />
            ) : (
              <p className="text-slate-400 text-sm text-center py-12">Need dated transactions to show chart</p>
            )}
          </div>

          {/* Recent transactions */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 lg:col-span-2 animate-fade-in-up">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Recent Transactions</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    <th className="pb-3 pr-4">Date</th>
                    <th className="pb-3 pr-4">Description</th>
                    <th className="pb-3 pr-4">Category</th>
                    <th className="pb-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {transactions.slice(0, 8).map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50 transition">
                      <td className="py-3 pr-4 text-slate-500 whitespace-nowrap">{t.date || '—'}</td>
                      <td className="py-3 pr-4 font-medium text-slate-700">{t.description}</td>
                      <td className="py-3 pr-4">
                        <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {t.category}
                        </span>
                      </td>
                      <td className={`py-3 text-right font-semibold ${t.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {t.amount >= 0 ? '+' : '-'}${Math.abs(t.amount).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Forecast preview */}
      {forecast && forecast.total_predicted > 0 && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg animate-fade-in-up">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">🔮</span>
            <h3 className="text-sm font-semibold opacity-90">30-Day Forecast</h3>
          </div>
          <p className="text-3xl font-bold">${forecast.total_predicted.toLocaleString()}</p>
          <p className="text-sm opacity-75 mt-1">
            Confidence: ${forecast.confidence_range.lower.toLocaleString()} – ${forecast.confidence_range.upper.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
